import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, IconButton, Link, Stack, Typography } from '@mui/material'
import { ArrowUpRightIcon, PlayIcon, RouteIcon, UsersIcon } from 'lucide-react'

import { Card, EMPTY_VALUE } from '@repo/ui'
import { ActivityTimeCell, EmptyState, Search, SelectField, Table } from 'components'
import { headerWithTerm } from 'components/display/Table/headerWithTerm'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useReferrerState } from 'hooks'
import { useCountriesQuery, useWebsiteSessions } from 'hooks/api'
import { ROUTES } from 'routes'
import type { DayRange, WebsiteSession } from 'services'

import { TrackedLinkModal, type TrackedLinkModalTarget } from '../../TrackedLinks/TrackedLinkModal'
import { AUDIENCE_LABELS, formatDuration, replayView, sessionTime } from '../websiteAnalyticsModel'
import { sessionSourceLabel, toVisitorPage } from '../visitorJourneyModel'
import { SectionNote, SourceNotices, ToneChip } from './shared'
import { CountryLabel, Muted, TrackedLinkLabel, cellSx } from './visitorCells'
import { WebsiteJourneyDrawer, type JourneyTarget } from './WebsiteJourneyDrawer'

/*
  VRG-712 — Visitor history (Analytics → Website → Visitors). Was a capped "newest 100"
  StatTable (VRG-640/689); now the shared Table, paged SERVER-side:

  - The API pages over a cached index of every matching session (PostHog rejects OFFSET for
    the personal API key this reads with), so `total` is every match in the range and a page is
    25 / 50 / 100 rows. Filters change the query, which resets to page 1 here.
  - Country (flag + name) and Tracked link are columns. The link comes from the session's
    `vera_invite` code joined to tracked_links.code — never from referrer text.
  - The row's Journey action opens the visitor's path through the site (WebsiteJourneyDrawer);
    the per-row page list that used to expand under the row moved there.
*/

const DEVICE_OPTIONS = [
  { value: '', label: 'All devices' },
  { value: 'Desktop', label: 'Desktop' },
  { value: 'Mobile', label: 'Mobile' },
  { value: 'Tablet', label: 'Tablet' },
]

const TimeValue = ({ session }: { session: WebsiteSession }) => {
  const { knownSeconds, unknownPages } = sessionTime(session.pages)
  if (session.pages === null || knownSeconds === null)
    return (
      <InfoTooltip term="website-time-on-page" text={session.pages === null ? 'Page times could not be read from PostHog for this list.' : undefined}>
        <Box component="span" sx={{ color: 'text.secondary', cursor: 'help' }} tabIndex={0}>
          {EMPTY_VALUE}
        </Box>
      </InfoTooltip>
    )
  return unknownPages ? (
    <InfoTooltip
      term="website-time-on-page"
      text={`At least this long: ${unknownPages} page${unknownPages === 1 ? "'s" : "s'"} time is unknown.`}
    >
      <Box component="span" sx={{ cursor: 'help' }} tabIndex={0}>
        {formatDuration(knownSeconds)}+
      </Box>
    </InfoTooltip>
  ) : (
    <>{formatDuration(knownSeconds)}</>
  )
}

const ReplayLink = ({ session }: { session: WebsiteSession }) => {
  const view = replayView(session.recording, Boolean(session.replayUrl))
  if (!view.show)
    return view.label ? (
      <InfoTooltip term={view.term} text={view.tooltip}>
        <Typography component="span" variant="body3" color="text.disabled" sx={{ cursor: 'help' }} tabIndex={0}>
          {view.label}
        </Typography>
      </InfoTooltip>
    ) : (
      <Typography variant="body2" color="text.disabled">
        {EMPTY_VALUE}
      </Typography>
    )
  return (
    <InfoTooltip text={view.label ? view.tooltip : 'Open the recording in PostHog'}>
      <Link
        href={session.replayUrl ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open the recording in PostHog"
        data-testid="replay-link"
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'inline-flex' }}
      >
        <PlayIcon size={14} />
      </Link>
    </InfoTooltip>
  )
}

export const WebsiteVisitorHistoryCard = ({ range, rangeLabel }: { range: DayRange; rangeLabel: string }) => {
  const [country, setCountry] = useState('')
  const [source, setSource] = useState('')
  const [page, setPage] = useState('')
  const [device, setDevice] = useState('')
  const [paging, setPaging] = useState({ page: 0, pageSize: 25 })
  const [journey, setJourney] = useState<JourneyTarget | null>(null)
  const [link, setLink] = useState<TrackedLinkModalTarget | null>(null)
  const referrer = useReferrerState('Analytics')

  // A filter change goes back to page 1 (the page the old filter's page 3 pointed at is gone).
  const filter = (set: (v: string) => void) => (v: string) => {
    set(v)
    setPaging((p) => ({ ...p, page: 0 }))
  }
  const { limit, offset } = toVisitorPage(paging)
  // Text filters arrive already debounced by the platform `Search`; selects change once per pick.
  const { data, isLoading, isFetching, error } = useWebsiteSessions(
    { ...range, country, source, page, device, limit: String(limit), offset: String(offset) },
    true,
  )
  const { data: countries } = useCountriesQuery()
  const countryNames = useMemo(
    () => new Map((countries ?? []).map((c) => [c.code.toUpperCase(), c.name])),
    [countries],
  )
  const countryOptions = useMemo(
    () => [
      { value: '', label: 'All countries' },
      ...(countries ?? [])
        .map((c) => ({ value: c.code.toUpperCase(), label: c.name, rightLabel: c.code.toUpperCase() }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [countries],
  )

  const open = (s: WebsiteSession) =>
    setJourney({ personId: s.personId, sessionId: s.sessionId, email: s.email, identified: s.identified })

  const columns = useMemo(
    () => [
      {
        field: 'startedAt',
        headerName: 'Started',
        width: 150,
        minWidth: 150,
        flex: 150,
        sortable: false,
        disableColumnMenu: true,
        pinned: 'left',
        renderCell: ({ row }: { row: WebsiteSession }) => <ActivityTimeCell at={row.startedAt} />,
      },
      {
        field: 'visitor',
        headerName: 'Visitor',
        width: 210,
        minWidth: 180,
        flex: 210,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
            {row.email ? (
              <Link component={RouterLink} to={ROUTES.INTERNAL_ACCESS} state={referrer} underline="hover" noWrap onClick={(e) => e.stopPropagation()} sx={{ typography: 'body2', minWidth: 0 }}>
                {row.email}
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary" noWrap>
                {row.identified ? 'Identified (admins see who)' : 'Anonymous'}
              </Typography>
            )}
            {row.personUrl ? (
              <InfoTooltip text="Open the PostHog person">
                <Link href={row.personUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the PostHog person" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex', flexShrink: 0 }}>
                  <ArrowUpRightIcon size={14} />
                </Link>
              </InfoTooltip>
            ) : null}
          </Stack>
        ),
      },
      {
        field: 'trackedLink',
        headerName: 'Tracked link',
        renderHeader: headerWithTerm('Tracked link', 'website-visitor-tracked-link'),
        width: 170,
        minWidth: 160,
        flex: 170,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => <TrackedLinkLabel link={row.trackedLink} code={row.inviteCode} onOpen={setLink} />,
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 140,
        minWidth: 120,
        flex: 140,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => <Muted>{sessionSourceLabel(row)}</Muted>,
      },
      {
        field: 'country',
        headerName: 'Country',
        width: 200,
        minWidth: 200,
        flex: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => <CountryLabel code={row.country} city={row.city} names={countryNames} />,
      },
      {
        field: 'entryPath',
        headerName: 'Landed on',
        width: 150,
        minWidth: 120,
        flex: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => <Muted title={row.entryPath ?? undefined}>{row.entryPath}</Muted>,
      },
      {
        field: 'pageviews',
        headerName: 'Pages',
        type: 'number',
        width: 80,
        minWidth: 76,
        flex: 80,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => <Muted>{String(row.pageviews)}</Muted>,
      },
      {
        field: 'time',
        headerName: 'Time',
        type: 'number',
        width: 96,
        minWidth: 90,
        flex: 96,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => (
          <Box sx={{ ...cellSx, justifyContent: 'flex-end', typography: 'body2' }}>
            <TimeValue session={row} />
          </Box>
        ),
      },
      {
        field: 'currentPath',
        headerName: 'Last page',
        width: 150,
        minWidth: 120,
        flex: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => <Muted title={row.currentPath ?? undefined}>{row.currentPath}</Muted>,
      },
      {
        field: 'device',
        headerName: 'Device',
        width: 100,
        minWidth: 96,
        flex: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => <Muted>{row.device}</Muted>,
      },
      {
        field: 'replay',
        headerName: 'Replay',
        width: 96,
        minWidth: 90,
        flex: 96,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: WebsiteSession }) => (
          <Box sx={cellSx}>
            <ReplayLink session={row} />
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: WebsiteSession }) => (
          <InfoTooltip text="Open this visitor's journey: every page, in order, with the time on each">
            <IconButton aria-label="Open journey" data-testid="visitor-journey-open" onClick={() => open(row)}>
              <RouteIcon size={18} strokeWidth={1} />
            </IconButton>
          </InfoTooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countryNames, referrer],
  )

  const rows = data?.sessions ?? []
  const total = data?.total ?? 0

  return (
    <Card title="Visitor history" action={<ToneChip tone="neutral" label="Admins only" />}>
      <Stack spacing={3} sx={{ mt: 4 }}>
        <SectionNote>
          {AUDIENCE_LABELS.consented} in {rangeLabel}, most recent activity first, {total.toLocaleString()} session
          {total === 1 ? '' : 's'}
          {data?.capped ? ' (PostHog returns at most 50,000; older sessions are past the end)' : ''}. Open a row&apos;s
          journey to see every page in order with the time on each. Identified visitors link to People; the play icon
          opens the recording in PostHog (kept 3 months).
        </SectionNote>
        {/* The same filter row the SEO keyword table uses, in a `minmax(0, 1fr)` grid so no field
            can push past the card at a phone width. Filters run on the server, before the count. */}
        <Box
          sx={{
            display: 'grid',
            gap: '12px',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          <SelectField value={country} onChange={(v) => filter(setCountry)(v as string)} options={countryOptions} search placeholder="All countries" />
          <Search value={source} onChange={filter(setSource)} onClear={() => filter(setSource)('')} placeholder="Source, e.g. linkedin" fullWidth sx={{ minWidth: 0 }} />
          <Search value={page} onChange={filter(setPage)} onClear={() => filter(setPage)('')} placeholder="Page, e.g. /roi" fullWidth sx={{ minWidth: 0 }} />
          <SelectField value={device} onChange={(v) => filter(setDevice)(v as string)} options={DEVICE_OPTIONS} placeholder="All devices" />
        </Box>
        {error ? (
          <SectionNote>
            {(error as { status?: number }).status === 403
              ? 'You cannot view visitor history.'
              : 'Could not load visitor history. Reload the page, or check the API is running.'}
          </SectionNote>
        ) : (
          <>
            {data ? <SourceNotices sources={[['PostHog', data.posthog]]} /> : null}
            {/* A definite box the table fills (`fillParent`), as on Tracked links: toolbar-free, header,
                rows at 72px, the pager. Capped so a 100-row page scrolls inside the card. */}
            <Box
              sx={{ height: Math.min(860, 120 + Math.max(3, rows.length) * 72), minWidth: 0, overflow: 'hidden' }}
              data-testid="visitor-history-table"
            >
              <Table
                loading={isLoading || (isFetching && !rows.length)}
                columns={columns}
                rows={rows}
                getRowId={(r: WebsiteSession) => r.sessionId}
                fillParent
                showActionLine={false}
                hideExport
                alwaysShowRowSelection={false}
                paginationMode="server"
                rowCount={total}
                paginationModel={paging}
                onPaginationModelChange={(model) => {
                  const next = toVisitorPage(model)
                  setPaging({ page: next.page, pageSize: next.pageSize })
                }}
                viewKey="internal-website-visitor-history"
                emptyState={
                  <EmptyState
                    title="No visitors match"
                    text="No analytics-on sessions match these filters in this range."
                    icon={<UsersIcon strokeWidth={1} size={32} />}
                  />
                }
              />
            </Box>
          </>
        )}
      </Stack>

      <WebsiteJourneyDrawer target={journey} range={range} rangeLabel={rangeLabel} onClose={() => setJourney(null)} onOpenLink={setLink} countryNames={countryNames} />
      <TrackedLinkModal link={link} onClose={() => setLink(null)} />
    </Card>
  )
}
